/**
 * Nonna Nonna bespoke dashboard.
 *
 * Visual goals (carried over from the Men's Health dashboard the owner wants
 * mirrored):
 *   • RTL layout
 *   • A row of equal-height cards, each card = one "segment" (product line,
 *     service, event type — owner's choice). Each card shows the segment's
 *     income, expenses and computed profit, plus quick inline edit fields.
 *   • A footer "totals" strip rolling up every card.
 *   • Add / rename / delete card actions inline — owner builds the board
 *     once and reuses it month after month.
 *
 * The backend (`/app/backend/routes/nonna.py`) keeps each card as a doc in
 * the `nonna_cards` collection tagged with `company_id`. That guarantees
 * cards are included in the dynamic v4 backup automatically.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, TrendingUp, TrendingDown, Wallet, Pencil, Check, X,
  ExternalLink,
} from "lucide-react";
import { api } from "../../lib/api";
import { ClientAdminSidebar } from "../../components/ClientAdminSidebar";
import { UndoRedoFab } from "../fullframe/UndoStack";

const ILS = (n) => `${Number(n || 0).toLocaleString("he-IL", { maximumFractionDigits: 0 })} ₪`;

// Default colour accents cycled when the user adds new cards.
const DEFAULT_COLORS = [
  "from-rose-500 to-rose-700",
  "from-amber-500 to-amber-700",
  "from-emerald-500 to-emerald-700",
  "from-sky-500 to-sky-700",
  "from-violet-500 to-violet-700",
  "from-fuchsia-500 to-fuchsia-700",
];

export default function NonnaDashboard() {
  const { slug } = useParams();
  const [companyId, setCompanyId] = useState(null);
  const [company, setCompany] = useState(null);
  const [cards, setCards] = useState([]);
  const [totals, setTotals] = useState({ income: 0, expenses: 0, profit: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Resolve slug → company_id once on mount.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/companies/by-slug/${encodeURIComponent(slug)}`);
        setCompanyId(data?.id || null);
        setCompany(data || null);
      } catch (e) {
        toast.error("חברה לא נמצאה");
      }
    })();
  }, [slug]);

  const load = async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      const { data } = await api.get(`/nonna/cards?company_id=${companyId}`);
      setCards(data.cards || []);
      setTotals(data.totals || { income: 0, expenses: 0, profit: 0, count: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "טעינת הכרטיסים נכשלה");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  const onAdd = async () => {
    if (!companyId) return;
    setCreating(true);
    try {
      const color = DEFAULT_COLORS[cards.length % DEFAULT_COLORS.length];
      await api.post(`/nonna/cards?company_id=${companyId}`, {
        name: `כרטיס ${cards.length + 1}`,
        color,
        income: 0,
        expenses: 0,
      });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "יצירה נכשלה");
    } finally {
      setCreating(false);
    }
  };

  const onUpdate = async (card, patch) => {
    try {
      await api.patch(`/nonna/cards/${card.id}?company_id=${companyId}`, patch);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שמירה נכשלה");
    }
  };

  const onDelete = async (card) => {
    if (!window.confirm(`למחוק את הכרטיס "${card.name}"?`)) return;
    try {
      await api.delete(`/nonna/cards/${card.id}?company_id=${companyId}`);
      await load();
      toast.success("הכרטיס נמחק");
    } catch (e) {
      toast.error(e.response?.data?.detail || "מחיקה נכשלה");
    }
  };

  return (
    <div dir="rtl" className="min-h-screen bg-gradient-to-b from-stone-50 to-stone-100" data-testid="nonna-dashboard">
      {/* ===== Header ===== */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {company?.logo_url && (
              <img src={company.logo_url} alt="" className="h-10 w-10 rounded-lg object-contain bg-stone-50" />
            )}
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-extrabold text-stone-900 truncate" data-testid="nonna-title">
                {company?.display_name || company?.name || "NONNA NONNA"}
              </h1>
              <p className="text-xs text-stone-500">דשבורד הכנסות והוצאות</p>
            </div>
          </div>
          <Link to="/S.ADMIN" className="hidden sm:inline-flex items-center gap-1 text-xs font-bold text-stone-500 hover:text-stone-900">
            <ExternalLink className="h-3.5 w-3.5" /> חזרה למרכז ניהול
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* ===== Totals strip ===== */}
        <TotalsStrip totals={totals} loading={loading} />

        {/* FlowUpSender hero button — REMOVED (FlowUp products were
            wiped at user request, 2026-05-26). */}

        {/* ===== Cards grid ===== */}
        <section data-testid="nonna-cards">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base sm:text-lg font-extrabold text-stone-900">כרטיסים</h2>
            <button
              type="button"
              onClick={onAdd}
              disabled={creating}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-stone-900 hover:bg-stone-800 text-white text-xs font-extrabold shadow-md disabled:opacity-50"
              data-testid="nonna-add-card"
            >
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              כרטיס חדש
            </button>
          </div>

          {loading ? (
            <div className="text-center py-10 text-stone-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
          ) : cards.length === 0 ? (
            <EmptyState onAdd={onAdd} creating={creating} />
          ) : (
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {cards.map((c) => (
                <NonnaCard
                  key={c.id}
                  card={c}
                  onUpdate={(patch) => onUpdate(c, patch)}
                  onDelete={() => onDelete(c)}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Admin sidebar — credits, top-ups, work history, backups, addons,
          API keys vault. Same component used in Full Frame. */}
      {company && <ClientAdminSidebar company={company} />}

      {/* Floating undo/redo controls — top-left corner, vertical. */}
      <div className="fixed top-20 left-4 z-[10100]">
        <UndoRedoFab />
      </div>
    </div>
  );
}

// ============== Totals strip ==============
function TotalsStrip({ totals, loading }) {
  const items = [
    { label: "סך הכנסות", value: totals.income, tone: "text-emerald-600", icon: TrendingUp, testid: "totals-income" },
    { label: "סך הוצאות", value: totals.expenses, tone: "text-rose-600", icon: TrendingDown, testid: "totals-expenses" },
    { label: "רווח נקי", value: totals.profit, tone: totals.profit >= 0 ? "text-emerald-700" : "text-rose-700", icon: Wallet, testid: "totals-profit" },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="nonna-totals">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <div key={it.label} className="rounded-2xl bg-white border border-stone-200 shadow-sm px-4 py-4 flex items-center justify-between" data-testid={it.testid}>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold">{it.label}</div>
              <div className={`text-2xl font-black mt-1 ${it.tone}`}>
                {loading ? "—" : ILS(it.value)}
              </div>
            </div>
            <Icon className={`h-8 w-8 ${it.tone} opacity-70`} />
          </div>
        );
      })}
    </div>
  );
}

// ============== Single card ==============
function NonnaCard({ card, onUpdate, onDelete }) {
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(card.name);
  const [income, setIncome] = useState(String(card.income ?? 0));
  const [expenses, setExpenses] = useState(String(card.expenses ?? 0));
  // Keep local state in sync with reloaded data.
  useEffect(() => {
    setNameDraft(card.name);
    setIncome(String(card.income ?? 0));
    setExpenses(String(card.expenses ?? 0));
  }, [card.id, card.name, card.income, card.expenses]);

  const profit = useMemo(() => Number(income || 0) - Number(expenses || 0), [income, expenses]);
  const color = card.color || "from-stone-700 to-stone-900";

  const commitName = async () => {
    const v = (nameDraft || "").trim();
    if (!v) { setNameDraft(card.name); setEditingName(false); return; }
    if (v === card.name) { setEditingName(false); return; }
    await onUpdate({ name: v });
    setEditingName(false);
  };

  const commitField = async (field, value) => {
    const num = Number(value || 0);
    if (num === Number(card[field] || 0)) return;
    await onUpdate({ [field]: num });
  };

  return (
    <article className={`rounded-2xl overflow-hidden bg-white border border-stone-200 shadow-sm hover:shadow-md transition`} data-testid={`nonna-card-${card.id}`}>
      {/* Coloured accent strip = the card identity */}
      <div className={`h-2 bg-gradient-to-l ${color}`} />

      <div className="p-4 space-y-3">
        {/* Title row — inline rename */}
        <div className="flex items-center justify-between gap-2">
          {editingName ? (
            <div className="flex items-center gap-1 flex-1">
              <input
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") commitName(); if (e.key === "Escape") { setNameDraft(card.name); setEditingName(false); } }}
                autoFocus
                className="flex-1 px-2 py-1 text-sm font-bold rounded-md border border-stone-300 focus:outline-none focus:ring-2 focus:ring-stone-900"
                data-testid={`nonna-card-name-input-${card.id}`}
              />
              <button type="button" onClick={commitName} className="p-1 rounded text-emerald-600 hover:bg-emerald-50" data-testid={`nonna-card-name-save-${card.id}`}>
                <Check className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => { setNameDraft(card.name); setEditingName(false); }} className="p-1 rounded text-stone-500 hover:bg-stone-100">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setEditingName(true)}
              className="flex items-center gap-1.5 text-base font-extrabold text-stone-900 hover:text-stone-700 group"
              data-testid={`nonna-card-name-${card.id}`}
              title="לחצי לעריכת שם"
            >
              <span className="truncate">{card.name}</span>
              <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-60 transition" />
            </button>
          )}
          <button
            type="button"
            onClick={onDelete}
            className="p-1.5 rounded-md text-stone-400 hover:text-rose-600 hover:bg-rose-50 transition"
            title="מחק כרטיס"
            data-testid={`nonna-card-delete-${card.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>

        {/* Income / Expenses inputs */}
        <FieldRow
          label="הכנסות"
          tone="text-emerald-700"
          value={income}
          onChange={setIncome}
          onCommit={() => commitField("income", income)}
          testid={`nonna-card-income-${card.id}`}
        />
        <FieldRow
          label="הוצאות"
          tone="text-rose-700"
          value={expenses}
          onChange={setExpenses}
          onCommit={() => commitField("expenses", expenses)}
          testid={`nonna-card-expenses-${card.id}`}
        />

        {/* Profit summary */}
        <div className={`mt-2 rounded-xl px-3 py-2 flex items-center justify-between ${profit >= 0 ? "bg-emerald-50" : "bg-rose-50"}`} data-testid={`nonna-card-profit-${card.id}`}>
          <span className={`text-[11px] uppercase tracking-widest font-bold ${profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>רווח</span>
          <span className={`text-lg font-black ${profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{ILS(profit)}</span>
        </div>
      </div>
    </article>
  );
}

function FieldRow({ label, tone, value, onChange, onCommit, testid }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <label className={`text-[11px] uppercase tracking-widest font-bold ${tone}`}>{label}</label>
      <div className="relative w-32">
        <input
          type="number"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onCommit}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          className={`w-full text-left tabular-nums text-sm font-bold rounded-lg border border-stone-200 bg-stone-50 px-3 py-1.5 pe-7 focus:outline-none focus:ring-2 focus:ring-stone-900 ${tone}`}
          data-testid={testid}
        />
        <span className="absolute inset-y-0 right-2 flex items-center text-xs text-stone-400">₪</span>
      </div>
    </div>
  );
}

// ============== Empty state ==============
function EmptyState({ onAdd, creating }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-stone-300 bg-white p-10 text-center" data-testid="nonna-empty">
      <div className="text-stone-400 text-5xl mb-3">＋</div>
      <h3 className="text-lg font-extrabold text-stone-900 mb-1">אין עדיין כרטיסים</h3>
      <p className="text-sm text-stone-500 max-w-md mx-auto mb-4">
        צרי כרטיס לכל קטגוריה / מוצר / פעילות עסקית — בכל כרטיס תזיני הכנסות והוצאות, והדשבורד יציג סך הכל אוטומטית.
      </p>
      <button
        type="button"
        onClick={onAdd}
        disabled={creating}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-stone-900 hover:bg-stone-800 text-white text-sm font-extrabold shadow-md disabled:opacity-50"
        data-testid="nonna-empty-add"
      >
        {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        כרטיס ראשון
      </button>
    </div>
  );
}
